import { Analytics } from'../helpers/analytics';
import { Scroll } from '../helpers/scroll';
import { ElementHelpers } from '../helpers/element-helpers';
import {hideHeader} from '../helpers/hide-header';

export class ImageGallery {
	constructor(elem) {
		this.elem = elem;
		this.slides = this.elem.querySelectorAll('.image-slider__slide');

		// Cancel if we have an empty draggable component
		if (!this.slides || this.slides.length === 0) return;

		this.startSlides = this.elem.querySelectorAll('[data-image-gallery-start]');

		this.analyticsCategory = 'imagegallery_component';

		this.slider = this.elem.querySelector('.image-slider');

		this.wrapper = this.elem.querySelector('.image-slider__wrapper');

		this.slideElem = this.elem.querySelector('.image-slider__slides');
		this.prevButton = this.elem.querySelector('.image-slider__control--prev');
		this.nextButton = this.elem.querySelector('.image-slider__control--next');
		this.closeButton = this.elem.querySelector('.image-slider__closebutton');

		this.thumbnailSlideElem = this.elem.querySelector('.image-slider__thumbnail-nav');
		this.thumbnails = this.elem.querySelectorAll('.image-slider__thumbnail-nav--single');

		this.initialized = false;

		this.cardsToShow = 1;

		this.transform = 0;

		this.transitionDuration = 500;

		let galleryHolder = document.querySelectorAll('.token-container.imageGallery');
		galleryHolder = galleryHolder[0];
		if (galleryHolder) {
			galleryHolder.innerHTML = this.elem.outerHTML;
			this.elem.innerHTML = '';
			ElementHelpers.removeElement(this.elem);
			galleryHolder.classList.remove('imageGallery');
			Analytics.initTrackLinkClicks(galleryHolder);
			new ImageGallery(galleryHolder.querySelector('[data-image-gallery]'));
		}

		this.addEventHandlers();
	}

	/**
	 * Get the current index
	 * @returns {*}
	 */
	get current() {
		return parseInt(this._current);
	}

	/**
	 * Set the new current index
	 * @param currentValue The newly value
	 */
	set current(currentValue) {
		this.onPropertyChanged('current', currentValue, (value) => {
			this._current = value;
		});
	}

	/**
	 * When our property 'current' is change, make sure that we set it appropriately.
	 * For example we can't have a 'current' lower than 0 or higher than the length of the cards array
	 * @param property
	 * @param val
	 * @param callback
	 */
	onPropertyChanged(property, val, callback) {
		let value;

		// Toggle our prevbutton
		if (val <= 0) {
			value = 0;
			this.prevButton.classList.add('hidden');
		} else {
			value = val;
			this.prevButton.classList.remove('hidden');
		}

		// Toggle our next button
		if (val >= (this.slides.length - 1)) {
			value = this.slides.length - 1;
			this.nextButton.classList.add('hidden');
		} else {
			value = val;
			this.nextButton.classList.remove('hidden');
		}

		// Execute passed callback
		if (typeof callback === 'function') {
			callback(value);
		}

		return value;
	}

	/**
	 * All necessary event handlers
	 */
	addEventHandlers() {
		for (let i = 0; i < this.startSlides.length; i++) {
			this.startSlides[i].addEventListener('click', (e) => {
				this.onStartSlide(e);
			});
		}

		this.closeButton.addEventListener('click', () => {
			this.onClose();
		});

		this.prevButton.addEventListener('click', () => {
			if (this.current !== 0) {
				this.onPrev();
			}
		});

		this.nextButton.addEventListener('click', () => {
			if (this.current !== this.slides.length - this.cardsToShow) {
				this.onNext();
			}
		});

		for (let i = 0; i < this.thumbnails.length; i++) {
			this.thumbnails[i].addEventListener('click', (e) => {
				this.clickThumbnail(e);
			});
		}

		window.addEventListener('resize', () => {
			if (this.initialized) {
				this.setSlideWidths();
				this.slideSlides(this.current);
			}
		});

		this.slideElem.addEventListener('click', () => {
			if (this.initialized) {
				this.toggleShowElements();
			}
		});

		/**
		 * TouchStart, TouchMove and TouchEnd functions, based on Willems carousel script
		 * in online-spa-widgets/documents-spa --> Carousel component
		 */
		this.wrapper.addEventListener('touchstart', (event) => {
			if (this.animating) {
				this.resetAnimation(this.slideElem);
			}
			event.preventDefault();
			this.elementWidth = event.currentTarget.clientWidth;
			this.beginTouchPosX = this.getTouchPosX(event) + this.getCurrentPosInPx();
		});

		this.wrapper.addEventListener('touchmove', (event) => {
			event.preventDefault();

			const $carouselContainer = this.slideElem;
			this.lastTouchPosXPercentage = this.calculateTouchPosXPercentage(this.getTouchPosX(event));
			let translatePercentage = this.lastTouchPosXPercentage;

			// when swipe to left on first slide
			if (translatePercentage <= 0) {
				translatePercentage = translatePercentage / 4;
				translatePercentage = Math.abs(translatePercentage);
				this.setTransformStyle($carouselContainer, 'translateX(' + translatePercentage + '%)');
				return;
			}

			// when swipe to right on last slide
			const beginTouchPercentage = Math.floor(this.beginTouchPosX / this.elementWidth) * 100;
			const lastItemPosition = this.getLastItemPosition();
			if (this.current === lastItemPosition && beginTouchPercentage <= this.lastTouchPosXPercentage) {
				const percentageDifference = translatePercentage - (lastItemPosition * 100);
				translatePercentage = (lastItemPosition * 100) + (percentageDifference / 4);
			}

			// DEFAULT: when swipe to left or right
			translatePercentage = '-' + translatePercentage;
			this.setTransformStyle($carouselContainer, 'translateX(' + translatePercentage + '%)');

		});

		this.wrapper.addEventListener('touchend', () => {
			this.animating = true;
			const $carouselContainer = this.slideElem;
			if (this.lastTouchPosXPercentage) {
				this.current = this.getNewPosition();
			} else {
				this.toggleShowElements();
			}
			$carouselContainer.classList.remove('stop-transition');
			this.slideSlides(this.current);
		});

		window.addEventListener('keydown', (e) => {
			const key = e.key;
			if (this.initialized) {
				this.onKeyDown(key);
			}
		});
	}

	/**
	 * Which function to fire for which pressed key
	 * @param key
	 */
	onKeyDown(key) {
		if (key === 'ArrowLeft' || key === 'Left') this.onPrev();
		if (key === 'ArrowRight' || key === 'Right') this.onNext();
		if (key === 'Escape' || key === 'Esc') this.onClose();
		if (key === ' ' || key === 'Spacebar') this.toggleShowElements();
	}

	/**
	 * Resets the animation to let the slider move fluently while swiping
	 * @param $carouselContainer
	 */
	resetAnimation($carouselContainer) {
		$carouselContainer.classList.add('stop-transition');
		this.animating = false;
		this.elementWidth = null;
		this.lastTouchPosXPercentage = null;
	}

	/**
	 * calculate touch position percentage based on the space between the begin touch position and current touch position
	 * @param newTouchPosX touch position x
	 * @returns number
	 */
	calculateTouchPosXPercentage(newTouchPosX) {
		const touchPositionDifference = this.beginTouchPosX - newTouchPosX;
		return (touchPositionDifference / this.elementWidth) * 100;
	}

	/**
	 * Get current position in pixels
	 * @returns {number}
	 */
	getCurrentPosInPx() {
		return this.current * this.elementWidth;
	}

	/**
	 * Get touch position
	 * @param event
	 * @returns {number}
	 */
	getTouchPosX(event) {
		return event.clientX || event.touches[0].clientX;
	}

	/**
	 * Get the position of the last item
	 * @returns {number}
	 */
	getLastItemPosition() {
		return this.slides.length - 1;
	}

	/**
	 * set inline transform style to containerElement
	 * @param $element containerElement
	 * @param value transform style
	 */
	setTransformStyle($element, value) {
		$element.style.transform = value;
		$element.style.webkitTransform = value;
	}

	/**
	 * determine new current position
	 * @returns currentPosition
	 */
	getNewPosition() {
		const currentPosPercentage = this.lastTouchPosXPercentage - (this.current * 100);

		if (currentPosPercentage < 0 && currentPosPercentage > -2) {
			this.toggleShowElements();
			return this.current;
		}

		if (currentPosPercentage > 0 && currentPosPercentage < 2) {
			this.toggleShowElements();
			return this.current;
		}

		if (currentPosPercentage < 0 && currentPosPercentage > -20) {
			return this.current;
		}

		if (currentPosPercentage > 0 && currentPosPercentage < 20) {
			return this.current;
		}

		this.slideOnSwipe(currentPosPercentage);

		return this.current;
	}

	/**
	 * Function to slide to prev or next on swipe.
	 * Function is made to remove complexity errors
	 * @param currentPosPercentage
	 */
	slideOnSwipe(currentPosPercentage) {
		if (currentPosPercentage > 0 && this.current < this.getLastItemPosition()) {
			this.onNext();
		}

		if (currentPosPercentage < 0 && this.current > 0) {
			this.onPrev();
		}
	}

	/**
	 * Initialize slider after clicking an image
	 * @param index
	 */
	initializeSlider(index = 0) {
		this.initialized = true;

		this.animating = true;

		this.slider.classList.add('modal--open');
		document.body.classList.add('overflow-hidden');
		document.getElementsByTagName('html')[0].classList.add('overflow-hidden');
		hideHeader();

		this.current = index;

		this.setSlideWidths();

		this.transform = index * -100;
		this.thumbnailTransform = this.thumbnailSlideWidth * index;

		this.slideSlides(index);
	}

	/**
	 * Set the width of a card so we can calculate the slide percentage
	 */
	setSlideWidths() {
		this.slideWidth = this.slides[0].offsetWidth;
		this.thumbnailSlideWidth = this.thumbnails[0].offsetWidth;
	}

	/**
	 * Go to previous slide or stay at the first one
	 */
	onPrev() {
		if (this.current !== 0) {
			this.thumbnails[this.current].classList.remove('active');
			this.current = this.current <= 0 ? 0 : this.current - 1;
			this.slideSlides(this.current);

			const analyticsData = {
				action: 'click - navigate',
				category: this.analyticsCategory,
				label: 'previous'
			};
			Analytics.measure(analyticsData);
		}
	}

	/**
	 * Go to the next slide or stay the the last one
	 */
	onNext() {
		if (this.current !== this.slides.length - this.cardsToShow) {
			this.thumbnails[this.current].classList.remove('active');
			this.current = this.current >= this.slides.length ? this.slides.length : this.current + 1;
			this.slideSlides(this.current);
			const analyticsData = {
				action: 'click - navigate',
				category: this.analyticsCategory,
				label: 'next'
			};
			Analytics.measure(analyticsData);
		}
	}

	/**
	 * Slide both slides; main slider and thumbnail slider
	 * @param index
	 */
	slideSlides(index) {
		this.slideMainSlider(index);
		this.slideThumbnail(index);
	}

	/**
	 * Slides the main slider
	 * @param index
	 */
	slideMainSlider(index) {
		this.transform = index * -100;
		this.slideElem.style.transform = `translateX(${this.transform}%)`;
		this.lazyLoadImage();
	}

	/**
	 * Slides the thumbnail slider
	 * @param index
	 */
	slideThumbnail(index) {
		this.thumbnailTransform = this.thumbnailSlideWidth * index;
		const width = this.thumbnailSlideWidth * this.thumbnails.length - 20 + 50;

		const maxWidth = width - this.slideWidth;

		if (this.thumbnailTransform > maxWidth) this.thumbnailTransform = maxWidth;

		// Added a timeout to the scroll effect, else the transition of .image-slider__slides would break
		setTimeout(() => {
			Scroll.scrollTo(this.thumbnailSlideElem, this.thumbnailTransform, this.transitionDuration);
		}, 10);
		this.setCurrentThumbnailActive(index);
	}

	/**
	 * Clickevent when clicking the thumbnail
	 * @param event
	 */
	clickThumbnail(event) {
		this.thumbnails[this.current].classList.remove('active');
		const index = event.currentTarget.dataset.imageGalleryIndex;
		this.current = index;
		this.slideSlides(index);
		const analyticsData = {
			action: 'click - navigate',
			category: this.analyticsCategory,
			label: 'thumbnail'
		};
		Analytics.measure(analyticsData);
	}

	/**
	 * Set current thumbnail to active, when initializing the slider, or when sliding the slides
	 * @param index
	 */
	setCurrentThumbnailActive(index) {
		this.thumbnails[index].classList.add('active');
	}

	/**
	 * Clickevent when clicked an image. In this event we initialize the slider
	 * @param event
	 */
	onStartSlide(event) {
		const index = event.currentTarget.dataset.imageGalleryStart;
		this.slider.classList.add('modal--open');
		this.initializeSlider(index);
	}

	/**
	 * Event to close and deinitialize the slider
	 */
	onClose() {
		this.slider.classList.remove('modal--open');
		this.thumbnails[this.current].classList.remove('active');
		document.body.classList.remove('overflow-hidden');
		document.getElementsByTagName('html')[0].classList.remove('overflow-hidden');

		const analyticsData = {
			action: 'click - interact',
			category: this.analyticsCategory,
			label: 'close'
		};
		Analytics.measure(analyticsData);

		this.initialized = false;
	}

	/**
	 * Toggles hiding the elements, like thumbnails, title, closebtn, etc.
	 */
	toggleShowElements() {
		this.slider.classList.toggle('image-slider--elements-hidden');
	}

	/**
	 * Lazy load image script based on https://medium.com/@filipvitas/lazy-load-images-with-zero-javascript-2c5bcb691274 (first technique)
	 */
	lazyLoadImage() {
		const img = this.slides[this.current].querySelector('img');
		if (!img.src) {
			img.src = img.dataset.lazyLoadSrc;
			img.onload = () => {
				img.classList.add('loaded');
			}
		}
	}

	static getSelector() {
		return '[data-image-gallery]';
	}

}
